import { createAction } from '@reduxjs/toolkit';

import { deleteQLog, generateLog, getAllLogs, QLog, QTask, updateLog } from '@qwealth/qdata';
import { deleteFiles, getPathContents } from '@qwealth/qvault';

import { errorHandler } from 'services/axiosService';
import { deleteHouseholdActivity } from './householdActivities';
import { getCallLogFilter, getTasksByExtendedFilters } from 'utils/activityFilterHelper';
import { LogFilterParam, QFeedTaskFilterParam } from '../models/ActivityFilters';
import { AppThunkDispatch } from '../store';
import { getTaskAttachmentPath } from 'data/helpers/taskHelper';
import { getLogAttachmentPath } from '../helpers/logHelper';
import { deleteTask, generateTask, updateTask } from 'data/data-layer/qtask';

const SET_ACTIVITIES_LOADING = 'SET_ACTIVITIES_LOADING';
const DELETE_QBOARD_TASK = 'DELETE_QBOARD_TASK';
const UPDATE_QBOARD_TASKS = 'UPDATE_QBOARD_TASKS';
const CREATE_QBOARD_CALL_LOG = 'CREATE_QBOARD_CALL_LOG';
const UPDATE_QBOARD_CALL_LOG = 'UPDATE_QBOARD_CALL_LOG';
const DELETE_QBOARD_CALL_LOG = 'DELETE_QBOARD_CALL_LOG';
const UPDATE_QBOARD_CALL_LOGS = 'UPDATE_QBOARD_CALL_LOGS';
const RESET_QBOARD_ACTIVITIES_DATA = 'RESET_QBOARD_ACTIVITIES_DATA';

export const setActivitiesLoading = createAction<boolean>(SET_ACTIVITIES_LOADING);

export const updateQBoardCallLogs = createAction<QLog[]>(UPDATE_QBOARD_CALL_LOGS);
export const createQBoardCallLogActivity = createAction<QLog>(CREATE_QBOARD_CALL_LOG);
export const updateQBoardCallLogActivity = createAction<QLog>(UPDATE_QBOARD_CALL_LOG);
export const deleteQBoardCallLogActivity = createAction<string>(DELETE_QBOARD_CALL_LOG);

export const updateQBoardTasks = createAction<QTask[]>(UPDATE_QBOARD_TASKS);
export const deleteQBoardTaskActivity = createAction<QTask[]>(DELETE_QBOARD_TASK);
export const resetQBoardActivitiesData = createAction<boolean>(RESET_QBOARD_ACTIVITIES_DATA);

export const loadCallLogs =
  (filters: LogFilterParam) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setActivitiesLoading(true));
    try {
      const callLogs: QLog[] = await getAllLogs(getCallLogFilter(filters));
      await dispatch(updateQBoardCallLogs(callLogs));
    } catch (error) {
      await errorHandler(dispatch, 'Failed to load call logs on QBoard')(error);
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  };

export const loadTasks =
  (filters: QFeedTaskFilterParam) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setActivitiesLoading(true));
    try {
      const tasks: QTask[] = await getTasksByExtendedFilters(filters);
      await dispatch(updateQBoardTasks(tasks));
    } catch (error) {
      await errorHandler(dispatch, 'Failed to load tasks on QBoard')(error);
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  };

export const saveActivityTask =
  (task: QTask, taskFilters: QFeedTaskFilterParam) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> =>
    task.id ? await dispatch(updateActivityTask(task, taskFilters)) : await dispatch(createActivityTask(task, taskFilters));

const createActivityTask =
  (task: QTask, taskFilters: QFeedTaskFilterParam) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> => {
    dispatch(setActivitiesLoading(true));
    try {
      const createdTask: QTask[] = await generateTask(task);
      const getTasks = await getTasksByExtendedFilters(taskFilters);
      await dispatch(updateQBoardTasks(getTasks));
      return createdTask;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to create a qboard task')(error);
      return null;
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  };

const updateActivityTask =
  (task: QTask, taskFilters: QFeedTaskFilterParam) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> => {
    dispatch(setActivitiesLoading(true));
    try {
      const updatedTask: QTask[] = await updateTask(task);
      const getTasks = await getTasksByExtendedFilters(taskFilters);
      dispatch(updateQBoardTasks(getTasks));
      return updatedTask;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to update a qboard task')(error);
      return null;
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  };

export const deleteTaskAttachments = async (task: QTask): Promise<void> => {
  if (task.id && task.householdQID) {
    const path = getTaskAttachmentPath(task.householdQID, task.id.toString());
    await deleteKeysFromPath(path);
  }
};

export const deleteActivityTask =
  (task: QTask) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setActivitiesLoading(true));
    try {
      const deletedTasks: QTask[] = await deleteTask(task);
      dispatch(deleteQBoardTaskActivity(deletedTasks));
      if (!('isRecurring' in task && task?.isRecurring)) {
        await deleteTaskAttachments(task);
      }
    } catch (error) {
      await errorHandler(dispatch, 'Failed to deleted a qboard task')(error);
    } finally {
      dispatch(setActivitiesLoading(false));
    }
  };

const createActivityLog =
  (qLog: QLog) =>
  async (dispatch: AppThunkDispatch): Promise<QLog | null> => {
    try {
      const createdLog = await generateLog(qLog);
      dispatch(createQBoardCallLogActivity(createdLog));

      return createdLog;
    } catch (error) {
      await errorHandler(dispatch, 'Creating activity failed')(error);
      return null;
    }
  };

const deleteKeysFromPath = async (path: string): Promise<void> => {
  let keys: string[] = [];
  await getPathContents(path).then(contentList => {
    if (contentList) {
      keys = contentList.filter(({ key }) => key?.includes(path)).map(({ key }) => key!);
    }
  });

  if (keys.length > 0) {
    await deleteFiles(keys);
  }
};

export const deleteLogAttachment = async (log: QLog): Promise<void> => {
  if (log?.uuid && log.owners && log?.owners.length > 0) {
    const path = getLogAttachmentPath(log.owners[0], log.uuid);
    await deleteKeysFromPath(path);
  }
};

export const deleteCallLog =
  (log: QLog) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    try {
      if (!log.uuid) {
        await errorHandler(dispatch, 'Failed to delete call log')();
        return;
      }
      await deleteQLog(log.uuid);
      //@ts-ignore
      dispatch(deleteHouseholdActivity(log));
      dispatch(deleteQBoardCallLogActivity(log.uuid));
      await deleteLogAttachment(log);
    } catch (err) {
      await errorHandler(dispatch, 'Failed to delete call log')(err);
    }
  };

export const saveActivityLog =
  (log: QLog) =>
  async (dispatch: AppThunkDispatch): Promise<QLog | null> =>
    log.uuid ? await dispatch(updateActivityLog(log)) : await dispatch(createActivityLog(log));

const updateActivityLog =
  (log: QLog) =>
  async (dispatch): Promise<QLog | null> => {
    try {
      const updatedLog = await updateLog(log);
      dispatch(updateQBoardCallLogActivity(updatedLog));
      return updatedLog;
    } catch (error) {
      await errorHandler(dispatch, 'Creating activity failed')(error);
      return null;
    }
  };

export const resetActivitiesData =
  () =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    await dispatch(resetQBoardActivitiesData(true));
  };
